import { isStampFabricObject } from '@/image-designer/adapters/stampAreasAdapter';
import { resolveStudioPreviewUrl } from '@/pages/ipe/workbench/utils/studioImagePreview';
import { getStampFrameBounds, applyStampFrameVisual, getStampFrame } from './stampLayerUtils';
import { applyStampPreviewEffects } from './stampPreviewEffects';

export function isStampPreviewObject(obj) {
  return obj?.linkData?.role === 'stampPreview';
}

export function isStampPreviewLikeObject(obj) {
  if (isStampPreviewObject(obj)) return true;
  return typeof obj?.id === 'string' && obj.id.startsWith('stamp-preview-');
}

export function getStampPreviewId(stampId) {
  return `stamp-preview-${stampId}`;
}

function parseObjectPosition(objectPosition) {
  return String(objectPosition || 'center center');
}

/** 计算印花图在矩形框内的绘制尺寸与偏移（与套图工作台逻辑一致） */
export function computeImageLayout(natW, natH, boxW, boxH, fillMode, objectPosition) {
  const posStr = parseObjectPosition(objectPosition);
  let drawW = boxW;
  let drawH = boxH;
  let offsetX = 0;
  let offsetY = 0;

  if (fillMode === 'stretch') {
    return { drawW: boxW, drawH: boxH, offsetX: 0, offsetY: 0 };
  }

  const natAspect = natW / natH;
  const boxAspect = boxW / boxH;

  if (fillMode === 'cover') {
    if (natAspect > boxAspect) {
      drawH = boxH;
      drawW = boxH * natAspect;
      if (posStr.includes('left')) offsetX = 0;
      else if (posStr.includes('right')) offsetX = boxW - drawW;
      else offsetX = (boxW - drawW) / 2;
      offsetY = 0;
    } else {
      drawW = boxW;
      drawH = boxW / natAspect;
      offsetX = 0;
      if (posStr.includes('top')) offsetY = 0;
      else if (posStr.includes('bottom')) offsetY = boxH - drawH;
      else offsetY = (boxH - drawH) / 2;
    }
    return { drawW, drawH, offsetX, offsetY };
  }

  // contain（适应印花区域）
  if (natAspect > boxAspect) {
    drawW = boxW;
    drawH = boxW / natAspect;
    offsetX = 0;
    if (posStr.includes('top')) offsetY = 0;
    else if (posStr.includes('bottom')) offsetY = boxH - drawH;
    else offsetY = (boxH - drawH) / 2;
  } else {
    drawH = boxH;
    drawW = boxH * natAspect;
    if (posStr.includes('left')) offsetX = 0;
    else if (posStr.includes('right')) offsetX = boxW - drawW;
    else offsetX = (boxW - drawW) / 2;
    offsetY = 0;
  }

  return { drawW, drawH, offsetX, offsetY };
}

/** 90°/270° 时在区域内旋转，布局按交换后的宽高计算 */
export function getStampLayoutDimensions(natW, natH, boxW, boxH, stampRotation = 0) {
  const rot = ((Number(stampRotation) || 0) % 360 + 360) % 360;
  if (rot === 90 || rot === 270) {
    return { layoutNatW: natH, layoutNatH: natW, boxW, boxH };
  }
  return { layoutNatW: natW, layoutNatH: natH, boxW, boxH };
}

export function computeStampPreviewScales(origNatW, origNatH, layout, stampRotation = 0) {
  const rot = ((Number(stampRotation) || 0) % 360 + 360) % 360;
  if (rot === 90 || rot === 270) {
    return {
      scaleX: layout.drawH / origNatW,
      scaleY: layout.drawW / origNatH,
    };
  }
  return {
    scaleX: layout.drawW / origNatW,
    scaleY: layout.drawH / origNatH,
  };
}

function buildStampPreviewClipPath(fabricNs, bounds) {
  return new fabricNs.Rect({
    width: bounds.width,
    height: bounds.height,
    originX: 'center',
    originY: 'center',
    left: bounds.centerX,
    top: bounds.centerY,
    angle: bounds.angle || 0,
    absolutePositioned: true,
  });
}

function applyPreviewLayout(img, layout, bounds, origNatW, origNatH, fabricNs) {
  const scaleX = layout.drawW / origNatW;
  const scaleY = layout.drawH / origNatH;
  const frameAngle = bounds.angle || 0;
  const localCenterOffsetX = layout.offsetX + layout.drawW / 2 - bounds.width / 2;
  const localCenterOffsetY = layout.offsetY + layout.drawH / 2 - bounds.height / 2;
  const rad = (frameAngle * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const offsetX = localCenterOffsetX * cos - localCenterOffsetY * sin;
  const offsetY = localCenterOffsetX * sin + localCenterOffsetY * cos;

  img.set({
    left: bounds.centerX + offsetX,
    top: bounds.centerY + offsetY,
    angle: frameAngle,
    originX: 'center',
    originY: 'center',
    scaleX,
    scaleY,
    clipPath: fabricNs ? buildStampPreviewClipPath(fabricNs, bounds) : undefined,
    selectable: false,
    evented: false,
    hasControls: false,
    flipX: false,
    flipY: false,
  });
}

function placeStampPreviewLayer(canvas, img, stampObj) {
  const frame = getStampFrame(stampObj) || stampObj;
  const frameIndex = canvas.getObjects().indexOf(frame);
  if (frameIndex >= 0) {
    canvas.moveTo(img, frameIndex);
    return;
  }
  const templateBase = canvas.getObjects().find((o) => o.linkData?.role === 'templateBase');
  if (templateBase) {
    canvas.moveTo(img, canvas.getObjects().indexOf(templateBase) + 1);
  }
}

/**
 * 同一张印花的预览图「在途加载」缓存。
 * 合成二次编辑里印花同步逻辑可能同时存在多份（左侧印花面板 + CompositeStampSyncHost），
 * 若各自发起一次异步 fromURL，就会在画布上留下多张同 id 的预览图副本——这些副本不可选中、
 * 无法移动，且旧的 removeStampPreview 只删第一张，于是画布上会永久残留一份「复制出来的印花」。
 * 这里用单例在途 Promise 保证同一张印花只会真正 add 一次。
 */
const inFlightPreviewLoads = new Map();

/** 收集画布上某张印花的所有预览图（可能因历史缺陷存在重复） */
export function collectStampPreviews(canvas, stampId) {
  if (!canvas?.getObjects) return [];
  const previewId = getStampPreviewId(stampId);
  return canvas.getObjects().filter((o) => o && o.id === previewId);
}

/** 移除某张印花的预览图：必须删干净，否则重复副本会残留且无法移动 */
export function removeStampPreview(canvas, stampId) {
  collectStampPreviews(canvas, stampId).forEach((preview) => canvas.remove(preview));
}

/** 清理重复的预览副本，只保留第一张 */
function dedupeStampPreviews(canvas, stampId) {
  const previews = collectStampPreviews(canvas, stampId);
  previews.slice(1).forEach((dup) => canvas.remove(dup));
  return previews[0] || null;
}

export function removeOrphanStampPreviews(canvas) {
  if (!canvas?.getObjects) return;
  const stampIds = new Set(
    canvas.getObjects().filter(isStampFabricObject).map((o) => o.id),
  );
  const seenPreviewIds = new Set();
  canvas.getObjects().filter(isStampPreviewObject).forEach((preview) => {
    const previewId = preview.id;
    const stampId = preview.linkData?.stampId;
    const orphan = !stampIds.has(stampId) || seenPreviewIds.has(previewId);
    if (orphan) {
      canvas.remove(preview);
      return;
    }
    seenPreviewIds.add(previewId);
  });
}

async function resolveStampExamplePreviewUrl(stampObj) {
  const url = String(stampObj?.linkData?.exampleImageUrl || '').trim();
  if (!url) return '';
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  const linkData = stampObj.linkData || {};
  return resolveStudioPreviewUrl(url, {
    displayUrl: linkData.exampleImageDisplayUrl,
    viewUrl: linkData.exampleImageViewUrl,
  });
}

export async function updateStampPreviewTransform(canvas, fabric, stampObj) {
  const url = stampObj.linkData?.exampleImageUrl;
  if (!url) return null;

  // 先清掉历史遗留的重复副本，保证画布上每张印花只有一个预览图
  const existing = dedupeStampPreviews(canvas, stampObj.id);
  const bounds = getStampFrameBounds(stampObj);
  if (!bounds) return null;

  const fillMode = stampObj.linkData?.fillMode || 'contain';
  const objectPosition = stampObj.linkData?.objectPosition || 'center center';

  const applyLayout = (img) => {
    const natW = img.width || 1;
    const natH = img.height || 1;
    const layout = computeImageLayout(
      natW,
      natH,
      bounds.width,
      bounds.height,
      fillMode,
      objectPosition,
    );
    applyPreviewLayout(img, layout, bounds, natW, natH, fabric);
    applyStampPreviewEffects(img, stampObj.linkData, { bounds });
    img.set({
      linkData: { role: 'stampPreview', stampId: stampObj.id },
    });
    img.setCoords();
    canvas.requestRenderAll();
    return img;
  };

  if (existing) {
    placeStampPreviewLayer(canvas, existing, stampObj);
    return applyLayout(existing);
  }

  const loadUrl = await resolveStampExamplePreviewUrl(stampObj);
  if (!loadUrl) return null;

  const previewId = getStampPreviewId(stampObj.id);
  let pending = inFlightPreviewLoads.get(previewId);
  if (!pending) {
    pending = new Promise((resolve) => {
      fabric.Image.fromURL(
        loadUrl,
        (img) => resolve(img || null),
        { crossOrigin: 'anonymous' },
      );
    });
    // 无论成功失败都要释放在途标记，避免后续同步被永久跳过
    pending = pending.finally(() => {
      inFlightPreviewLoads.delete(previewId);
    });
    inFlightPreviewLoads.set(previewId, pending);
  }

  const img = await pending;
  if (!img) return null;

  // 等待期间可能已被其它同步实例加过预览：复用，绝不能再加一份
  const raced = dedupeStampPreviews(canvas, stampObj.id);
  if (raced) {
    placeStampPreviewLayer(canvas, raced, stampObj);
    return applyLayout(raced);
  }

  img.set({
    id: previewId,
    linkData: { role: 'stampPreview', stampId: stampObj.id },
  });
  canvas.add(img);
  placeStampPreviewLayer(canvas, img, stampObj);
  return applyLayout(img);
}

export async function syncStampPreview(canvas, fabric, stampObj) {
  if (!canvas || !fabric || !stampObj) return null;
  const url = stampObj.linkData?.exampleImageUrl;
  removeStampPreview(canvas, stampObj.id);

  if (!url) {
    applyStampFrameVisual(stampObj, canvas);
    canvas.requestRenderAll();
    return null;
  }

  applyStampFrameVisual(stampObj, canvas);
  return await updateStampPreviewTransform(canvas, fabric, stampObj);
}

export async function syncAllStampPreviews(canvas, fabric) {
  if (!canvas || !fabric) return;
  removeOrphanStampPreviews(canvas);
  const stamps = canvas.getObjects().filter(isStampFabricObject);
  await Promise.all(stamps.map((stamp) => syncStampPreview(canvas, fabric, stamp)));
  canvas.requestRenderAll();
}

/** 保存模板前移除运行时预览图层（示例 URL 已存在 linkData 中） */
export function stripStampPreviewObjectsFromFabricJson(fabricJson) {
  if (!fabricJson?.objects) return fabricJson;
  return {
    ...fabricJson,
    objects: fabricJson.objects.filter((obj) => !isStampPreviewLikeObject(obj)),
  };
}

export function collectStampExampleMetaFromFabricJson(fabricJson) {
  const exampleImages = [];
  const stampExampleImages = {};
  const seen = new Set();

  (fabricJson?.objects || []).forEach((obj) => {
    if (!isStampFabricObject(obj)) return;
    const url = obj.linkData?.exampleImageUrl;
    const imageId = obj.linkData?.exampleImageId;
    if (!url) return;
    stampExampleImages[obj.id] = url;
    if (imageId && !seen.has(imageId)) {
      seen.add(imageId);
      exampleImages.push({
        id: imageId,
        url,
        dataUrl: url.startsWith('data:') ? url : undefined,
        name: obj.linkData?.exampleImageName || '示例图',
        isExample: true,
      });
    }
  });

  return { exampleImages, stampExampleImages };
}
